import { translate } from '#packages/i18n';
import * as util from '#packages/util';
import coreUtilsLib from 'coreUtilsLib';

import { extractValuableText } from './utils';
import { getEssentialData } from '../hiddenElementsStateManagementUtils';
import { getGhostComponentType } from './ghostComponents';

import type { HiddenElementsScope } from '../hiddenElementsScope';
import type { HiddenElement } from '../hiddenElementsTypes';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from 'types/documentServices';
export const buildHiddenElement = (
  scope: HiddenElementsScope,
  compId: string,
  pageId: string,
): HiddenElement => {
  const { editorAPI } = scope;

  if (isGhostComponent(editorAPI, compId, pageId)) {
    // in case you don't know what is referred/ghost components:
    // https://wix.slack.com/archives/C9N00LLE6/p1730965278539889
    return buildGhostElement(editorAPI, compId, pageId);
  }

  const isMobileOnly =
    editorAPI.mobile.mobileOnlyComponents.isMobileOnlyComponent(compId);
  if (isMobileOnly) {
    return buildMobileOnlyHiddenElement(editorAPI, compId, pageId);
  }

  const desktopCompRef = editorAPI.components.get.byId(
    compId,
    pageId,
    editorAPI.dsRead.viewMode.VIEW_MODES.DESKTOP,
  );

  const compType = editorAPI.components.getType(desktopCompRef);
  const desktopLayout = editorAPI.components.layout.get_rect(desktopCompRef);

  return {
    pageId,
    compId,
    compType,
    desktopLayout,
    label: getDisplayText(editorAPI, desktopCompRef, compType),
    icon: getIcon(editorAPI, desktopCompRef, compType),
    ...getEssentialData(editorAPI, desktopCompRef),
  };
};

export const buildInteractionElement = (
  scope: HiddenElementsScope,
  compRef: CompRef,
  pageRef: CompRef,
) => {
  const { editorAPI } = scope;

  return {
    isInteraction: true,
    pageId: pageRef.id,
    compId: compRef.id,
    compType: editorAPI.components.getType(compRef),
    label: editorAPI.components.getDisplayName(compRef),
    icon: editorAPI.components.getIconInfo(compRef),
  };
};

const isGhostComponent = (
  editorAPI: EditorAPI,
  compId: string,
  pageId: string,
): boolean => {
  const compRef = editorAPI.components.get.byId(compId, pageId, 'MOBILE');
  return coreUtilsLib.displayedOnlyStructureUtil.isRefPointer(compRef);
};

const buildMobileOnlyHiddenElement = (
  editorAPI: EditorAPI,
  compId: string,
  pageId: string,
): HiddenElement => {
  const { className, label } = getAttrsForMobileOnlyElement(compId);

  return {
    pageId,
    compId,
    compType: compId,
    label: translate(label),
    icon: getIcon(editorAPI, null, className),
  };
};

const getAttrsForMobileOnlyElement = (compId: string) => {
  switch (compId) {
    case 'TINY_MENU':
      return {
        className: 'wysiwyg.viewer.components.mobile.TinyMenu',
        label: 'component_label_tinyMenu',
      };
    case 'MENU_AS_CONTAINER_TOGGLE':
      return {
        className: 'wysiwyg.viewer.components.mobile.TinyMenu',
        label: 'component_label_MenuToggle',
      };
    case 'MENU_AS_CONTAINER_EXPANDABLE_MENU':
      return {
        className: 'wysiwyg.viewer.components.mobile.TinyMenu',
        label: 'component_label_expandableMenu',
      };
    default:
      throw new Error(`Unknown mobile only component: ${compId}`);
  }
};

const getIcon = (
  editorAPI: EditorAPI,
  compRef: CompRef | null,
  compType: string,
) => {
  const iconSize = { width: 50, height: 50 };
  return util.compIcon.getCompIconInfo(
    compRef,
    compType,
    iconSize,
    editorAPI,
    '',
  );
};

const getDisplayText = (
  editorAPI: EditorAPI,
  compRef: CompRef,
  compType: string,
): string => {
  let labelText = editorAPI.components.getDisplayName(compRef, compType);
  const compData = editorAPI.components.data.get(compRef);

  if (compType === 'wysiwyg.viewer.components.SiteButton' && compData?.label) {
    labelText += `: ${compData.label}`;
  }

  if (compType === 'wysiwyg.viewer.components.WRichText' && compData?.text) {
    labelText += `: ${compData.text}`;
  }

  return extractValuableText(labelText);
};

const buildGhostElement = (
  editorAPI: EditorAPI,
  compId: string,
  pageId: string,
): HiddenElement => {
  const ghostRef = editorAPI.components.get.byId(compId, pageId, 'MOBILE');

  if (
    coreUtilsLib.displayedOnlyStructureUtil.isRepeatedComponent(ghostRef.id)
  ) {
    ghostRef.id = coreUtilsLib.displayedOnlyStructureUtil.getRepeaterTemplateId(
      ghostRef.id,
    );
  }

  const compType = getGhostComponentType(editorAPI, ghostRef);
  const desktopLayout = editorAPI.components.layout.get_rect(ghostRef);

  return {
    compId: ghostRef.id,
    pageId,
    compType,
    desktopLayout,
    label: getDisplayText(editorAPI, ghostRef, compType),
    icon: getIcon(editorAPI, ghostRef, compType),
  };
};
